import { useId } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormInputWrapper, isRequiredLabel } from "@apac/form-ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { parseDayMonthAndYear } from "@hopper-b2b/utilities";
import { DateInput } from "./DateInput";
import styles from "./styles.module.scss";

export type DateFormatter = (date: string) => string;

export interface DateFieldProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  dateFormatter?: DateFormatter;
}

const formatInputDate: DateFormatter = (date) => {
  const { month, day, year, strLength } = parseDayMonthAndYear(date);

  return (
    day + (strLength > 2 ? "/" : "") + month + (strLength > 4 ? "/" : "") + year
  );
};

export const DateField: React.FC<DateFieldProps> = ({
  formContext,
  placeholder = "DD/MM/YYYY",
  dateFormatter = formatInputDate,
  required,
  ...props
}) => {
  const [block] = useModuleBEM(styles, "DateField");

  // Associate the visible label with the input via htmlFor/id (WCAG 1.3.1 / 4.1.2).
  const inputId = useId();
  const ariaRequired = isRequiredLabel(props.label, required);

  return (
    <div className={block}>
      <FormInputWrapper
        {...props}
        htmlFor={inputId}
        control={formContext.control}
        render={({ field, fieldState }) => (
          <DateInput
            {...field}
            id={inputId}
            placeholder={placeholder}
            dateFormatter={dateFormatter}
            aria-required={ariaRequired || undefined}
            aria-invalid={fieldState.invalid || undefined}
          />
        )}
      />
    </div>
  );
};
